// HoursReport.jsx

// Dependencies
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTable, useSortBy, useFilters } from 'react-table';

// Components
import HoursDisplay from '@/components/data/hours/HoursDisplay';
import MondayDate from '@/components/data/date/MondayDate';
import DateSelector from '@/components/data/date/DateSelector';
import { professionalengagementsCss } from '@digital_monorepo/styling';

const HoursReport = ({ report }) => {
  const { t } = useTranslation();

  const [date, setDate] = useState(new MondayDate());
  const [showDate, setShowDate] = useState(true);
  const [filteredHoursReport, setFilteredHoursReport] = useState(
    report.filter((item) => item.StartDate === date.getSQLDate())
  );

  const onPreviousWeek = () => {
    const newDate = new MondayDate(date);
    newDate.previousMonday();
    setDate(newDate);
  };

  const onNextWeek = () => {
    const newDate = new MondayDate(date);
    newDate.nextMonday();
    setDate(newDate);
  };

  useEffect(() => {
    const filtered = report.filter((item) => item.StartDate === date.getSQLDate());
    setFilteredHoursReport(filtered);
  }, [report, date]);

  const columns = useMemo(
    () => [
      {
        Header: t('HOURS_REPORT_TABLE.HEADERS.PROFESSIONAL_ID'),
        accessor: 'ProfessionalID',
        Cell: ({ value }) => <Link to={`/professionals/${value}`}>{value}</Link>,
      },
      {
        Header: t('HOURS_REPORT_TABLE.HEADERS.PROFESSIONAL_NAME'),
        accessor: 'ProfessionalName',
      },
      {
        Header: t('HOURS_REPORT_TABLE.HEADERS.FUNCTION_NAME'),
        accessor: 'FunctionName',
      },
      {
        Header: t('HOURS_REPORT_TABLE.HEADERS.OFFICE'),
        accessor: 'Office',
      },
      {
        Header: t('HOURS_REPORT_TABLE.HEADERS.HOURS'),
        accessor: 'Hours',
        Cell: ({ value }) => <HoursDisplay hours={value} />,
      },
    ],
    [t]
  );

  const data = useMemo(() => filteredHoursReport, [filteredHoursReport]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({ columns, data }, useFilters, useSortBy);

  return (
    <div className={professionalengagementsCss.sectionContainer}>
      <div className={professionalengagementsCss.sectionHeader}>
        <h3 className={professionalengagementsCss.sectionTitle}>
          {t('HOURS_REPORT_TABLE.TITLE')}
        </h3>
      </div>
      <div className={professionalengagementsCss.tableWrapper}>
        <div className={professionalengagementsCss.tableContainer}>
          <table {...getTableProps()} className={professionalengagementsCss.table}>
            <thead>
              {headerGroups.map((headerGroup) => {
                const { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps();
                return (
                  <tr key={key} {...restHeaderGroupProps} className={professionalengagementsCss.headerRow}>
                    {headerGroup.headers.map((column) => {
                      const { key, ...restColumn } = column.getHeaderProps(column.getSortByToggleProps());
                      return (
                        <th
                          key={key}
                          {...restColumn}
                          className={professionalengagementsCss.th}
                        >
                          <div style={{ marginBottom: '0.5rem' }}>
                            {column.render('Header')}
                            <span>
                              {column.isSorted
                                ? column.isSortedDesc
                                  ? ' 🔽'
                                  : ' 🔼'
                                : ''}
                            </span>
                          </div>
                          {column.canFilter && (
                            <input
                              type="text"
                              value={column.filterValue || ''}
                              onChange={(e) => column.setFilter(e.target.value || undefined)}
                              placeholder={`Filter...`}
                              style={{
                                width: '100%',
                                padding: '0.25rem',
                                fontSize: '0.75rem',
                                border: '1px solid #ddd',
                                borderRadius: '3px'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                    No hours data matches the current filters
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  prepareRow(row);
                  const { key, ...restRowProps } = row.getRowProps();
                  return (
                    <tr key={key} {...restRowProps} className={professionalengagementsCss.bodyRow}>
                      {row.cells.map((cell) => {
                        const { key, ...restCellProps } = cell.getCellProps();
                        return (
                          <td
                            key={key}
                            {...restCellProps}
                            className={professionalengagementsCss.td}
                          >
                            {cell.render('Cell')}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      {showDate && (
        <DateSelector
          date={date}
          onPreviousWeek={onPreviousWeek}
          onNextWeek={onNextWeek}
        />
      )}
    </div>
  );
};

export default HoursReport;
